import React, { useEffect, useRef } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { Button } from './button';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    description?: string;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    showCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    description,
    children,
    size = 'md',
    showCloseButton = true
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    // Handle escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    // Focus trap
    useEffect(() => {
        if (isOpen && modalRef.current) {
            const focusable = modalRef.current.querySelectorAll<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable.length > 0) {
                focusable[0].focus();
            }
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl'
    };

    return (
        <div
            className="fixed top-0 bottom-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
            style={{ left: 'var(--sidebar-left, 0px)', right: 'var(--sidebar-right, 0px)' }}
            onClick={onClose}
        >
            <div
                ref={modalRef}
                className={`bg-zinc-900 border border-white/10 rounded-xl w-full ${sizeClasses[size]} max-h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                {(title || showCloseButton) && (
                    <div className="flex items-start justify-between p-4 border-b border-white/5">
                        <div className="flex-1 min-w-0 pr-4">
                            {title && (
                                <h2 className="text-lg font-semibold text-zinc-100">{title}</h2>
                            )}
                            {description && (
                                <p className="text-sm text-zinc-500 mt-1">{description}</p>
                            )}
                        </div>
                        {showCloseButton && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onClose}
                                className="h-8 w-8 p-0 text-zinc-400 hover:text-white -mr-2 -mt-1"
                            >
                                <XIcon className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>
    );
};

// Input Modal for prompts
interface InputModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (value: string) => void;
    title: string;
    description?: string;
    placeholder?: string;
    defaultValue?: string;
    confirmText?: string;
    cancelText?: string;
}

export const InputModal: React.FC<InputModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    placeholder,
    defaultValue = '',
    confirmText = 'Confirm',
    cancelText = 'Cancel'
}) => {
    const [value, setValue] = React.useState(defaultValue);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setValue(defaultValue);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen, defaultValue]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onConfirm(value);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} description={description} size="sm">
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-zinc-100 placeholder:text-zinc-600 outline-none focus:border-indigo-500/50 transition-colors"
                />
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={onClose}>
                        {cancelText}
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-500 text-white">
                        {confirmText}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

// Confirm Modal
interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'default' | 'danger';
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'default'
}) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} description={description} size="sm">
            <div className="p-4 flex justify-end gap-2">
                <Button variant="ghost" onClick={onClose}>
                    {cancelText}
                </Button>
                <Button
                    onClick={() => { onConfirm(); onClose(); }}
                    className={variant === 'danger'
                        ? 'bg-red-600 hover:bg-red-500 text-white'
                        : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    }
                >
                    {confirmText}
                </Button>
            </div>
        </Modal>
    );
};
